import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaStar, FaSearch, FaTimes, FaCheckCircle, FaCheck, FaSyncAlt } from "react-icons/fa";
import { useThemeClasses } from "../../hooks/useThemeClasses";

// ─── Deterministic colour from brand name ───────────────────────────────────
const PALETTE = [
  ['#ef4444', '#fca5a5'],
  ['#3b82f6', '#93c5fd'],
  ['#10b981', '#6ee7b7'],
  ['#f59e0b', '#fcd34d'],
  ['#8b5cf6', '#c4b5fd'],
  ['#f97316', '#fdba74'],
  ['#06b6d4', '#67e8f9'],
  ['#ec4899', '#f9a8d4'],
  ['#84cc16', '#bef264'],
  ['#6366f1', '#a5b4fc'],
  ['#14b8a6', '#5eead4'],
  ['#ca8a04', '#fde68a'],
];

const getBrandColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

const getInitials = (name) =>
  name.replace(/[^A-Za-z0-9]/g, ' ').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() ||
  name.slice(0, 2).toUpperCase();

// ─── Featured brand data ─────────────────────────────────────────────────────
const FEATURED = [
  { name: 'BOSCH',          badge: 'PREMIUM',   badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',   desc: 'Global leader in automotive components and technology',         rating: 4.9, count: 25510, country: 'Germany',     href: '/brands/362-bosch/'         },
  { name: 'DENSO',          badge: 'TOP RATED', badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Premium OEM supplier for Toyota, Honda & more',                 rating: 4.8, count: 2430,  country: 'Japan',       href: '/brands/639-denso/'         },
  { name: 'MAHLE ORIGINAL', badge: 'PREMIUM',   badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',   desc: 'Filtration & engine component specialists',                     rating: 4.7, count: 2717,  country: 'Germany',     href: '/brands/1641-mahleoriginal/'},
  { name: 'Castrol',        badge: 'TOP RATED', badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Worlds leading engine oil manufacturer',                       rating: 4.8, count: 207,   country: 'UK',          href: '/brands/6319-castrol/'      },
  { name: 'BREMBO',         badge: 'PREMIUM',   badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',   desc: 'World-class braking systems for performance vehicles',          rating: 4.9, count: 2943,  country: 'Italy',       href: '/brands/385-brembo/'        },
  { name: 'NGK',            badge: 'TOP RATED', badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Leading spark plug & sensor manufacturer',                     rating: 4.8, count: 5318,  country: 'Japan',       href: '/brands/1867-ngk/'          },
  { name: 'VALEO',          badge: 'PREMIUM',   badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',   desc: 'Innovative automotive supplier & technology partner',           rating: 4.7, count: 8641,  country: 'France',      href: '/brands/2622-valeo/'        },
  { name: 'Shell',          badge: 'TOP RATED', badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Premium engine oils and lubricants for every vehicle',         rating: 4.7, count: 140,   country: 'Netherlands', href: '/brands/3489-shell/'        },
];

// ─── Category filter pills ────────────────────────────────────────────────────
const CATEGORIES = [
  { label: 'All Brands', icon: '🏷️', value: 'All'       },
  { label: 'Engine Oil', icon: '🛢️', value: 'Oil'       },
  { label: 'Filters',    icon: '🔧', value: 'Filters'   },
  { label: 'Brakes',     icon: '🔴', value: 'Brakes'    },
  { label: 'Electrical', icon: '⚡', value: 'Electrical'},
  { label: 'Suspension', icon: '🔩', value: 'Suspension'},
  { label: 'Tyres',      icon: '🛞', value: 'Tyres'     },
  { label: 'AC Parts',   icon: '❄️', value: 'AC Parts'  },
  { label: 'Body Parts', icon: '🚗', value: 'Body Parts'},
];

// ─── Alphabet nav ─────────────────────────────────────────────────────────────
const ALPHABETS = [
  'All', '0-9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'W', 'Z',
];

const getFirstLetter = (name) => {
  const c = name[0].toUpperCase();
  return (c >= '0' && c <= '9') ? '0-9' : c;
};

// ─── All brands data ──────────────────────────────────────────────────────────
const ALL_BRANDS = [
  { name: '3M',               count: 162,    href: '/brands/6399-3m/',               category: 'Accessories', country: 'USA',         rating: 4.3 },
  { name: 'ABE',              count: 50,     href: '/brands/19-abe/',                category: 'Brakes',      country: 'Germany',     rating: 4.1 },
  { name: 'ABRO',             count: 181,    href: '/brands/6358-abro/',             category: 'Accessories', country: 'USA',         rating: 4.0 },
  { name: 'A.B.S.',           count: 66,     href: '/brands/10-abs/',                category: 'Brakes',      country: 'Germany',     rating: 4.2 },
  { name: 'Accurate',         count: 4639,   href: '/brands/6437-accurate/',         category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'AFRO',             count: 319,    href: '/brands/6405-afro/',             category: 'General',     country: 'India',       rating: 3.9 },
  { name: 'Airpro',           count: 125,    href: '/brands/6348-airpro/',           category: 'Accessories', country: 'India',       rating: 4.1 },
  { name: 'AIS',              count: 60,     href: '/brands/6462-ais/',              category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'AJUSA',            count: 32,     href: '/brands/68-ajusa/',              category: 'Gaskets',     country: 'Spain',       rating: 4.2 },
  { name: 'AKSMIT',           count: 60,     href: '/brands/6439-aksmit/',           category: 'General',     country: 'India',       rating: 3.9 },
  { name: 'AKT',              count: 231,    href: '/brands/6382-akt/',              category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'Alaska',           count: 149,    href: '/brands/6483-alaska/',           category: 'Accessories', country: 'India',       rating: 3.8 },
  { name: 'Allied Westlake',  count: 146,    href: '/brands/6401-allied_westlake/',  category: 'Oil',         country: 'India',       rating: 4.0 },
  { name: 'ALP',              count: 366,    href: '/brands/6569-alp/',              category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'ALPINE',           count: 83,     href: '/brands/6448-alpine/',           category: 'Audio',       country: 'Japan',       rating: 4.3 },
  { name: 'AMP',              count: 917,    href: '/brands/4214-amp/',              category: 'Electrical',  country: 'India',       rating: 4.1 },
  { name: 'AMSOIL',           count: 65,     href: '/brands/6547-amsoil/',           category: 'Oil',         country: 'USA',         rating: 4.5 },
  { name: 'Anchem',           count: 15,     href: '/brands/6344-anchem/',           category: 'Accessories', country: 'India',       rating: 3.9 },
  { name: 'Anupam Industries',count: 429,    href: '/brands/6423-anupam_industries/',category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'ASHIKA',           count: 144,    href: '/brands/153-ashika/',            category: 'Brakes',      country: 'Japan',       rating: 4.2 },
  { name: 'ASHOK LEYLAND',    count: 7,      href: '/brands/154-ashokleyland/',      category: 'OEM',         country: 'India',       rating: 4.0 },
  { name: 'Auto Equip',       count: 475,    href: '/brands/6289-auto_equip/',       category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'Autofurnish',      count: 897,    href: '/brands/6346-autofurnish/',      category: 'Accessories', country: 'India',       rating: 4.2 },
  { name: 'Auto Gold',        count: 491,    href: '/brands/6364-auto_gold/',        category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'Autokoi',          count: 1186,   href: '/brands/6457-autokoi/',          category: 'General',     country: 'India',       rating: 4.1 },
  { name: 'Auto Oprema',      count: 1577,   href: '/brands/6430-auto_oprema/',      category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'AUX',              count: 756,    href: '/brands/6496-aux/',              category: 'Audio',       country: 'China',       rating: 3.8 },
  { name: 'BANCO',            count: 269,    href: '/brands/6136-banco/',            category: 'General',     country: 'India',       rating: 4.0 },
  { name: 'BANDO',            count: 1641,   href: '/brands/249-bando/',             category: 'Belts',       country: 'Japan',       rating: 4.4 },
  { name: 'BEHR',             count: 957,    href: '/brands/280-behr/',              category: 'Cooling',     country: 'Germany',     rating: 4.4 },
  { name: 'BLUE PRINT',       count: 70,     href: '/brands/337-blueprint/',         category: 'General',     country: 'UK',          rating: 4.2 },
  { name: 'BMW',              count: 154142, href: '/brands/343-bmw/',              category: 'OEM',         country: 'Germany',     rating: 4.8, featured: true },
  { name: 'BOSCH',            count: 25510,  href: '/brands/362-bosch/',             category: 'Electrical',  country: 'Germany',     rating: 4.9, featured: true },
  { name: 'BREMBO',           count: 2943,   href: '/brands/385-brembo/',            category: 'Brakes',      country: 'Italy',       rating: 4.9, featured: true },
  { name: 'Castrol',          count: 207,    href: '/brands/6319-castrol/',          category: 'Oil',         country: 'UK',          rating: 4.8, featured: true },
  { name: 'CHAMPION',         count: 2003,   href: '/brands/501-champion/',          category: 'Electrical',  country: 'USA',         rating: 4.3 },
  { name: 'Chemical Guys',    count: 91,     href: '/brands/6512-chemical_guys/',    category: 'Accessories', country: 'USA',         rating: 4.4 },
  { name: 'CHEVROLET',        count: 61359,  href: '/brands/1924-opelgmchevrolet/', category: 'OEM',         country: 'USA',         rating: 4.3 },
  { name: 'CONTITECH',        count: 5028,   href: '/brands/561-contitech/',         category: 'Belts',       country: 'Germany',     rating: 4.5 },
  { name: 'CORTECO',          count: 42,     href: '/brands/575-corteco/',           category: 'Gaskets',     country: 'Germany',     rating: 4.2 },
  { name: 'DAYCO',            count: 292,    href: '/brands/612-dayco/',             category: 'Belts',       country: 'Italy',       rating: 4.3 },
  { name: 'DELPHI',           count: 6902,   href: '/brands/630-delphi/',            category: 'Electrical',  country: 'USA',         rating: 4.6, featured: true },
  { name: 'DENSO',            count: 2430,   href: '/brands/639-denso/',             category: 'Electrical',  country: 'Japan',       rating: 4.8, featured: true },
  { name: 'Depo',             count: 2276,   href: '/brands/4858-depo/',             category: 'Lighting',    country: 'Taiwan',      rating: 4.2 },
  { name: 'Elegant',          count: 1989,   href: '/brands/6300-elegant/',          category: 'Accessories', country: 'India',       rating: 4.1 },
  { name: 'Elofic',           count: 401,    href: '/brands/5935-elofic/',           category: 'Filters',     country: 'India',       rating: 4.2 },
  { name: 'ELRING',           count: 50,     href: '/brands/770-elring/',            category: 'Gaskets',     country: 'Germany',     rating: 4.3 },
  { name: 'EXEDY',            count: 814,    href: '/brands/814-exedy/',             category: 'Clutch',      country: 'Japan',       rating: 4.5, featured: true },
  { name: 'Febest',           count: 121,    href: '/brands/3107-febest/',           category: 'General',     country: 'Japan',       rating: 4.2 },
  { name: 'FEBI BILSTEIN',    count: 388,    href: '/brands/849-febibilstein/',      category: 'General',     country: 'Germany',     rating: 4.3 },
  { name: 'FENNER',           count: 945,    href: '/brands/2830-fenner/',           category: 'Belts',       country: 'UK',          rating: 4.3 },
  { name: 'FERODO',           count: 2168,   href: '/brands/861-ferodo/',            category: 'Brakes',      country: 'France',      rating: 4.4 },
  { name: 'FORD',             count: 153873, href: '/brands/912-ford/',             category: 'OEM',         country: 'USA',         rating: 4.5, featured: true },
  { name: 'GABRIEL',          count: 2467,   href: '/brands/968-gabriel/',           category: 'Suspension',  country: 'USA',         rating: 4.3 },
  { name: 'GARRETT',          count: 3224,   href: '/brands/977-garrett/',           category: 'Turbo',       country: 'USA',         rating: 4.6 },
  { name: 'GATES',            count: 7536,   href: '/brands/980-gates/',             category: 'Belts',       country: 'USA',         rating: 4.6, featured: true },
  { name: 'GKN',              count: 7785,   href: '/brands/4315-gkn/',             category: 'Driveline',   country: 'UK',          rating: 4.5 },
  { name: 'GOETZE',           count: 7230,   href: '/brands/1030-goetze/',           category: 'Engine',      country: 'Germany',     rating: 4.4 },
  { name: 'GOODYEAR',         count: 56,     href: '/brands/6537-goodyear/',         category: 'Tyres',       country: 'USA',         rating: 4.5 },
  { name: 'HELLA',            count: 10060,  href: '/brands/1166-hella/',            category: 'Lighting',    country: 'Germany',     rating: 4.6, featured: true },
  { name: 'HITACHI',          count: 441,    href: '/brands/1205-hitachi/',          category: 'Electrical',  country: 'Japan',       rating: 4.4 },
  { name: 'Honda',            count: 81995,  href: '/brands/1222-hondaacura/',      category: 'OEM',         country: 'Japan',       rating: 4.7, featured: true },
  { name: 'HUCO',             count: 2255,   href: '/brands/1236-huco/',             category: 'Electrical',  country: 'Germany',     rating: 4.2 },
  { name: 'KYB',              count: 26,     href: '/brands/1501-kyb/',              category: 'Suspension',  country: 'Japan',       rating: 4.5 },
  { name: 'LAND ROVER',       count: 9517,   href: '/brands/1524-landrover/',        category: 'OEM',         country: 'UK',          rating: 4.5 },
  { name: 'LEMFORDER',        count: 1581,   href: '/brands/1550-lemforder/',        category: 'Suspension',  country: 'Germany',     rating: 4.5 },
  { name: 'Liqui Moly',       count: 244,    href: '/brands/6335-liqui_moly/',       category: 'Oil',         country: 'Germany',     rating: 4.7, featured: true },
  { name: 'LUCAS - TVS',      count: 1080,   href: '/brands/6306-lucas_tvs/',        category: 'Electrical',  country: 'India',       rating: 4.3 },
  { name: 'Lumax',            count: 1994,   href: '/brands/2819-lumax/',            category: 'Lighting',    country: 'India',       rating: 4.2 },
  { name: 'MAHINDRA',         count: 133459, href: '/brands/1637-mahindra/',        category: 'OEM',         country: 'India',       rating: 4.4 },
  { name: 'MAHLE ORIGINAL',   count: 2717,   href: '/brands/1641-mahleoriginal/',    category: 'Filters',     country: 'Germany',     rating: 4.7, featured: true },
  { name: 'MANN-FILTER',      count: 2329,   href: '/brands/1651-mannfilter/',       category: 'Filters',     country: 'Germany',     rating: 4.6 },
  { name: 'MARUTI SUZUKI',    count: 126368, href: '/brands/2456-suzukimaruti/',    category: 'OEM',         country: 'India',       rating: 4.5 },
  { name: 'MERCEDES-BENZ',    count: 209729, href: '/brands/1720-mercedesbenz/',    category: 'OEM',         country: 'Germany',     rating: 4.8, featured: true },
  { name: 'MEYLE',            count: 8381,   href: '/brands/1741-meyle/',            category: 'General',     country: 'Germany',     rating: 4.4 },
  { name: 'MOBIS (Hyundai, Kia)', count: 157432, href: '/brands/1254-hyundaikia/', category: 'OEM',         country: 'South Korea', rating: 4.6 },
  { name: 'Mobil',            count: 178,    href: '/brands/6318-mobil/',            category: 'Oil',         country: 'USA',         rating: 4.6, featured: true },
  { name: 'MONROE',           count: 4113,   href: '/brands/1779-monroe/',           category: 'Suspension',  country: 'USA',         rating: 4.5, featured: true },
  { name: 'MOTUL',            count: 41,     href: '/brands/6321-motul/',            category: 'Oil',         country: 'France',      rating: 4.7 },
  { name: 'myTVS',            count: 732,    href: '/brands/6445-mytvs/',            category: 'Service',     country: 'India',       rating: 4.2 },
  { name: 'NGK',              count: 5318,   href: '/brands/1867-ngk/',              category: 'Electrical',  country: 'Japan',       rating: 4.8, featured: true },
  { name: 'NiBK',             count: 42,     href: '/brands/1868-nibk/',             category: 'Brakes',      country: 'Japan',       rating: 4.3 },
  { name: 'Nissan / Renault', count: 93302,  href: '/brands/1872-nissaninfiniti/',  category: 'OEM',         country: 'Japan',       rating: 4.5 },
  { name: 'OPTIBELT',         count: 3806,   href: '/brands/1927-optibelt/',         category: 'Belts',       country: 'Germany',     rating: 4.4 },
  { name: 'OSRAM',            count: 603,    href: '/brands/1941-osram/',            category: 'Lighting',    country: 'Germany',     rating: 4.5 },
  { name: 'PHILIPS',          count: 903,    href: '/brands/2043-philips/',          category: 'Lighting',    country: 'Netherlands', rating: 4.5, featured: true },
  { name: 'PUROLATOR',        count: 762,    href: '/brands/2105-purolator/',        category: 'Filters',     country: 'USA',         rating: 4.3 },
  { name: 'RANE',             count: 1913,   href: '/brands/5957-rane/',             category: 'Steering',    country: 'India',       rating: 4.3 },
  { name: 'SACHS',            count: 395,    href: '/brands/2232-sachs/',            category: 'Suspension',  country: 'Germany',     rating: 4.4 },
  { name: 'Schaeffler (LuK, INA, FAG)', count: 4770, href: '/brands/1615-luk/',   category: 'Bearings',    country: 'Germany',     rating: 4.6 },
  { name: 'Shell',            count: 140,    href: '/brands/3489-shell/',            category: 'Oil',         country: 'Netherlands', rating: 4.7, featured: true },
  { name: 'SKF',              count: 3830,   href: '/brands/2367-skf/',              category: 'Bearings',    country: 'Sweden',      rating: 4.7, featured: true },
  { name: 'Spark Minda',      count: 1354,   href: '/brands/5931-sparkminda/',       category: 'Electrical',  country: 'India',       rating: 4.3 },
  { name: 'Subros',           count: 776,    href: '/brands/6138-subros/',           category: 'Cooling',     country: 'India',       rating: 4.2 },
  { name: 'Talbros',          count: 2294,   href: '/brands/5941-talbros/',          category: 'Gaskets',     country: 'India',       rating: 4.3 },
  { name: 'TATA',             count: 103988, href: '/brands/2478-tatatelco/',       category: 'OEM',         country: 'India',       rating: 4.4 },
  { name: 'TEXTAR',           count: 3101,   href: '/brands/2508-textar/',           category: 'Brakes',      country: 'Germany',     rating: 4.5 },
  { name: 'Toyota / Lexus',   count: 148274, href: '/brands/2545-toyotalexus/',     category: 'OEM',         country: 'Japan',       rating: 4.8, featured: true },
  { name: 'TRW',              count: 918,    href: '/brands/2571-trw/',              category: 'Brakes',      country: 'USA',         rating: 4.4 },
  { name: 'Turtle Wax',       count: 228,    href: '/brands/6340-turtle_wax/',       category: 'Accessories', country: 'USA',         rating: 4.3 },
  { name: 'TVS Girling',      count: 1956,   href: '/brands/6294-tvs_girling/',      category: 'Brakes',      country: 'India',       rating: 4.3 },
  { name: 'UFI',              count: 732,    href: '/brands/2592-ufi/',              category: 'Filters',     country: 'Italy',       rating: 4.3 },
  { name: 'UNO MINDA',        count: 3560,   href: '/brands/2818-unominda/',         category: 'Electrical',  country: 'India',       rating: 4.3 },
  { name: 'VAG (VW, AUDI, SKODA)', count: 142078, href: '/brands/2681-vwaudiskodaseatvag/', category: 'OEM', country: 'Germany',   rating: 4.7 },
  { name: 'VALEO',            count: 8641,   href: '/brands/2622-valeo/',            category: 'Electrical',  country: 'France',      rating: 4.7, featured: true },
  { name: 'Valvoline',        count: 6,      href: '/brands/6475-valvoline/',        category: 'Oil',         country: 'USA',         rating: 4.5 },
  { name: 'VOLVO',            count: 69980,  href: '/brands/2673-volvo/',            category: 'OEM',         country: 'Sweden',      rating: 4.6 },
  { name: 'WALKER',           count: 519,    href: '/brands/2696-walker/',           category: 'Exhaust',     country: 'USA',         rating: 4.2 },
  { name: 'WIX FILTERS',      count: 316,    href: '/brands/2746-wixfilters/',       category: 'Filters',     country: 'USA',         rating: 4.3 },
  { name: 'WURTH',            count: 67,     href: '/brands/6407-wurth/',            category: 'Accessories', country: 'Germany',     rating: 4.5 },
  { name: 'ZF',               count: 49,     href: '/brands/2799-zf/',               category: 'Transmission',country: 'Germany',     rating: 4.5 },
  { name: 'ZIP',              count: 750,    href: '/brands/5952-zip/',              category: 'General',     country: 'India',       rating: 4.0 },
];

// ─── Hover helpers ────────────────────────────────────────────────────────────
const glowOn  = e => { e.currentTarget.style.border = '1.5px solid #3b82f6'; e.currentTarget.style.boxShadow = '0 0 0 2px #3b82f6, 0 0 24px 4px rgba(59,130,246,0.35), 0 8px 32px rgba(0,0,0,0.4)'; };
const glowOff = e => { e.currentTarget.style.border = '1px solid #1e293b';    e.currentTarget.style.boxShadow = 'none'; };

// ─── Featured Card ────────────────────────────────────────────────────────────
const FeaturedCard = ({ brand }) => {
  const [bg, text] = getBrandColor(brand.name);
  const initials = getInitials(brand.name);

  return (
    <Link
      to={brand.href}
      className="group relative flex flex-col gap-2 sm:gap-4 rounded-xl sm:rounded-2xl border border-slate-800 bg-[#0d1425] p-3 sm:p-6 transition-all duration-300 sm:hover:-translate-y-1"
      onMouseEnter={glowOn}
      onMouseLeave={glowOff}
    >
      <div
        className={
          'self-start flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[10px] sm:tracking-widest ' +
          brand.badgeColor
        }
      >
        <span className="hidden sm:inline">{brand.badge === 'PREMIUM' ? '🏆' : '⭐'}</span>
        {brand.badge}
      </div>

      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-black shadow-lg transition-transform duration-300 group-hover:scale-110 sm:h-14 sm:w-14 sm:rounded-2xl sm:text-base"
        style={{
          background: 'radial-gradient(circle at 35% 35%, ' + text + ', ' + bg + ')',
          color: '#fff',
          textShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }}
      >
        {initials}
      </div>

      <div className="min-w-0">
        <h3 className="mb-0.5 truncate text-sm font-black text-white transition-colors group-hover:text-amber-400 sm:mb-1 sm:text-xl">
          {brand.name}
        </h3>
        <p className="hidden text-xs leading-relaxed text-slate-400 sm:line-clamp-2 sm:block">
          {brand.desc}
        </p>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-[9px] font-medium text-slate-400 sm:gap-3 sm:text-[11px]">
        <span className="flex items-center gap-0.5 sm:gap-1">
          <FaStar className="text-yellow-400" size={9} />
          <span className="font-bold text-slate-300">{brand.rating.toFixed(1)}</span>
        </span>
        <span className="hidden h-1 w-1 rounded-full bg-slate-700 sm:inline-block" />
        <span className="tabular-nums">{brand.count.toLocaleString()} parts</span>
        <span className="hidden h-1 w-1 rounded-full bg-slate-700 sm:inline-block" />
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
          {brand.country}
        </span>
      </div>
    </Link>
  );
};

// ─── Small Brand Card ─────────────────────────────────────────────────────────
const BrandCard = ({ brand }) => {
  const [bg, text] = getBrandColor(brand.name);
  const initials = getInitials(brand.name);

  return (
    <Link
      to={brand.href}
      className="group relative flex aspect-square flex-col items-center justify-between gap-1 rounded-xl border border-slate-800 bg-[#0d1425] p-2.5 transition-all duration-300 sm:aspect-auto sm:gap-2.5 sm:rounded-2xl sm:p-5 sm:hover:-translate-y-1"
      onMouseEnter={glowOn}
      onMouseLeave={glowOff}
    >
      {brand.featured && (
        <div className="absolute right-1.5 top-1.5 text-emerald-400 sm:right-3 sm:top-3">
          <FaCheckCircle size={11} />
        </div>
      )}

      <div
        className="flex h-9 w-9 items-center justify-center rounded-xl text-[10px] font-black shadow-lg transition-transform duration-300 group-hover:scale-110 sm:mb-0.5 sm:h-14 sm:w-14 sm:rounded-2xl sm:text-base"
        style={{
          background: 'radial-gradient(circle at 35% 35%, ' + text + ', ' + bg + ')',
          color: '#fff',
          textShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }}
      >
        {initials}
      </div>

      <h3 className="line-clamp-2 w-full text-center text-[10px] font-bold leading-tight text-slate-100 transition-colors group-hover:text-amber-400 sm:text-sm">
        {brand.name}
      </h3>

      <p className="hidden text-center text-[11px] leading-tight text-slate-500 sm:block">
        {brand.category} • {brand.country}
      </p>

      <div className="flex items-center gap-1">
        <FaStar className="text-yellow-400" size={9} />
        <span className="text-[9px] font-bold text-slate-300 sm:text-xs">
          {(brand.rating || 4.0).toFixed(1)}
          <span className="hidden sm:inline"> / 5.0</span>
        </span>
      </div>

      <p className="text-[9px] font-medium text-slate-500 sm:text-[11px]">
        {(brand.count || 0).toLocaleString()}
        <span className="hidden sm:inline"> products</span>
      </p>
    </Link>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const Brands = () => {
  const [filter, setFilter]               = useState('');
  const [selectedLetter, setSelectedLetter] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { isDark, sc } = useThemeClasses();

  const filteredBrands = ALL_BRANDS
    .filter(b => selectedLetter === 'All' || getFirstLetter(b.name) === selectedLetter)
    .filter(b => selectedCategory === 'All' || b.category === selectedCategory)
    .filter(b => b.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const featuredVisible = selectedCategory === 'All'
    ? FEATURED
    : FEATURED.filter(fb => ALL_BRANDS.find(ab => ab.name === fb.name && ab.category === selectedCategory));

  return (
    <div className={sc("brandsShell")}>

      {/* ── SCROLLING ANNOUNCEMENT TICKER ── */}
      <div className="bg-cyan-500 overflow-hidden">
        <style>{`
          @keyframes ticker {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ticker-track { display: flex; animation: ticker 28s linear infinite; width: max-content; }
          .ticker-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="ticker-track py-2 text-[11px] font-bold text-black/90 uppercase tracking-wide select-none">
          {[
            '🚚 FREE DELIVERY ON ORDERS ABOVE ₹999',
            '↩ EASY 7-DAY RETURNS',
            '♻ USED PARTS AVAILABLE AT 40-60% LESS',
            '🏆 200+ TRUSTED BRANDS — OEM & AFTERMARKET',
            '🚚 FREE DELIVERY ON ORDERS ABOVE ₹999',
            '↩ EASY 7-DAY RETURNS',
            '♻ USED PARTS AVAILABLE AT 40-60% LESS',
            '🏆 200+ TRUSTED BRANDS — OEM & AFTERMARKET',
          ].map((msg, i) => (
            <span key={i} className="px-8 border-r border-black/20 whitespace-nowrap">{msg}</span>
          ))}
        </div>
      </div>

      {/* ── HERO ── */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6 sm:pt-12 lg:pt-16 pb-6 sm:pb-10 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="mb-3 text-2xl font-black leading-tight sm:mb-5 sm:text-4xl md:text-5xl lg:text-6xl">
            Explore{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              200+ Trusted
            </span>
            <br className="hidden sm:block" />{' '}
            Auto Part Brands
          </h1>
          <p className="mx-auto mb-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:mb-10 sm:text-base md:text-lg">
            From OEM giants to trusted aftermarket leaders — find genuine parts
            from brands your mechanics recommend.
          </p>

          {/* Hero Search */}
          <div className="relative mx-auto mb-8 max-w-lg sm:mb-14">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 sm:pl-5">
              <FaSearch size={14} className="text-slate-500" />
            </div>
            <input
              type="text"
              placeholder="Search brands — Bosch, Denso, Mahle..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-700/60 bg-[#111827] py-3 pl-11 pr-10 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-500 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 sm:rounded-2xl sm:py-4 sm:pl-13 sm:pr-5"
              style={{ paddingLeft: '2.75rem' }}
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter('')}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 transition-colors hover:text-slate-300"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="mx-auto grid max-w-3xl grid-cols-4 gap-2 sm:gap-4">
            {[
              { value: '200+', label: 'BRANDS', color: 'text-amber-400' },
              { value: '50K+', label: 'PRODUCTS', color: 'text-orange-400' },
              { value: '98%', label: 'GENUINE', color: 'text-amber-400' },
              { value: '4.8★', label: 'AVG RATING', color: 'text-orange-400' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-slate-800 bg-[#111827] px-1 py-3 sm:rounded-2xl sm:px-4 sm:py-7"
              >
                <p className={'text-base font-black sm:text-3xl ' + stat.color}>{stat.value}</p>
                <p className="mt-0.5 text-[8px] font-bold tracking-wider text-slate-500 sm:mt-1 sm:text-[10px] sm:tracking-widest">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── REST OF PAGE ── */}
      <div className="mx-auto max-w-7xl space-y-8 px-3 pb-24 pt-2 sm:space-y-10 sm:px-6 sm:pb-16 sm:pt-4 lg:px-8">

        {/* ── Category Pill Bar ── */}
        <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1 scrollbar-hide sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setSelectedCategory(cat.value)}
              className={
                'flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition-all sm:px-4 sm:py-2 sm:text-sm ' +
                (selectedCategory === cat.value
                  ? 'border-amber-500 bg-amber-500 text-slate-900 shadow-lg shadow-amber-900/30'
                  : 'border-slate-700 bg-[#0d1425] text-slate-400 hover:border-slate-500 hover:text-slate-200')
              }
            >
              <span>{cat.icon}</span> {cat.label}
            </button>
          ))}
        </div>

        {/* ── Featured Brands ── */}
        {featuredVisible.length > 0 && (
          <section>
            <div className="mb-3 flex items-center justify-between sm:mb-5">
              <h2 className="flex items-center gap-2 text-base font-black sm:text-xl">
                <span>⭐</span> Featured Brands
              </h2>
              <button
                type="button"
                className="text-xs font-bold text-amber-400 transition-colors hover:text-amber-300 sm:text-sm"
              >
                See All →
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
              {featuredVisible.map((b) => (
                <FeaturedCard key={b.href} brand={b} />
              ))}
            </div>
          </section>
        )}

        {/* ── All Brands ── */}
        <section>
          <div className="mb-3 flex flex-col justify-between gap-3 sm:mb-5 sm:flex-row sm:items-center sm:gap-4">
            <h2 className="flex items-center gap-2 text-base font-black sm:text-xl">
              🏷️ All Brands
            </h2>
            <div className="flex items-center gap-3">
              <span className="shrink-0 text-xs font-bold text-amber-400 sm:text-sm">
                {filteredBrands.length} brands
              </span>
              <div className="relative w-full group sm:w-60">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-amber-400">
                  <FaSearch size={13} />
                </div>
                <input
                  type="text"
                  placeholder="Search brands..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-[#0d1425] py-2.5 pl-9 pr-9 text-sm text-slate-200 outline-none transition-all placeholder:text-slate-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30"
                />
                {filter && (
                  <button
                    type="button"
                    onClick={() => setFilter('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 transition-colors hover:text-slate-300"
                  >
                    <FaTimes size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Alphabet filter */}
          <div className="-mx-3 mb-4 flex gap-1.5 overflow-x-auto px-3 pb-1 scrollbar-hide sm:mx-0 sm:mb-6 sm:flex-wrap sm:gap-2 sm:overflow-visible sm:px-0">
            {ALPHABETS.map((letter) => (
              <button
                key={letter}
                type="button"
                onClick={() => setSelectedLetter(letter)}
                className={
                  'shrink-0 rounded-lg border px-2.5 py-1 text-[10px] font-bold transition-all sm:px-3 sm:py-1.5 sm:text-xs ' +
                  (selectedLetter === letter
                    ? 'border-amber-500 bg-amber-500 text-slate-900 shadow-lg shadow-amber-900/30'
                    : 'border-slate-700 bg-[#0d1425] text-slate-400 hover:border-amber-500/40 hover:text-slate-200')
                }
              >
                {letter}
              </button>
            ))}
          </div>

          {filteredBrands.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              <div className="mb-4 text-5xl">🔍</div>
              <p className="text-lg font-bold">No brands found for "{filter}"</p>
              <p className="mt-1 text-sm">Try a different search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {filteredBrands.map((brand) => (
                <BrandCard key={brand.href} brand={brand} />
              ))}
            </div>
          )}
        </section>

      </div>

      {/* ── TRUSTED BRANDS MARQUEE ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className={`border-t border-b py-10 overflow-hidden relative ${isDark ? 'border-slate-800/60' : 'border-slate-200'}`}>
          <p className={`text-center text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] mb-10 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
            Trusted by Mechanics & Garages across India
          </p>
          
          {/* Gradient fades for edges */}
          <div className={`absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r z-10 pointer-events-none ${isDark ? 'from-[#0a0d1a] to-transparent' : 'from-slate-50 to-transparent'}`}></div>
          <div className={`absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l z-10 pointer-events-none ${isDark ? 'from-[#0a0d1a] to-transparent' : 'from-slate-50 to-transparent'}`}></div>

          <style>{`
            @keyframes scroll-brands-ticker {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .brand-marquee-track {
              display: flex;
              white-space: nowrap;
              animation: scroll-brands-ticker 30s linear infinite;
              width: max-content;
            }
            .brand-marquee-track:hover {
              animation-play-state: paused;
            }
          `}</style>

          <div className="brand-marquee-track flex items-center gap-14 sm:gap-24 px-8">
            {[
              { name: 'Denso',   light: '#14b8a6', dark: '#0f766e' },
              { name: 'Mahle',   light: '#34d399', dark: '#047857' },
              { name: 'Castrol', light: '#facc15', dark: '#a16207' },
              { name: 'Brembo',  light: '#f87171', dark: '#b91c1c' },
              { name: 'MRF',     light: '#a8a29e', dark: '#44403c' },
              { name: 'Valeo',   light: '#8b5cf6', dark: '#4c1d95' },
              { name: 'Monroe',  light: '#fb923c', dark: '#c2410c' },
              
              { name: 'Denso',   light: '#14b8a6', dark: '#0f766e' },
              { name: 'Mahle',   light: '#34d399', dark: '#047857' },
              { name: 'Castrol', light: '#facc15', dark: '#a16207' },
              { name: 'Brembo',  light: '#f87171', dark: '#b91c1c' },
              { name: 'MRF',     light: '#a8a29e', dark: '#44403c' },
              { name: 'Valeo',   light: '#8b5cf6', dark: '#4c1d95' },
              { name: 'Monroe',  light: '#fb923c', dark: '#c2410c' },
              
              { name: 'Denso',   light: '#14b8a6', dark: '#0f766e' },
              { name: 'Mahle',   light: '#34d399', dark: '#047857' },
              { name: 'Castrol', light: '#facc15', dark: '#a16207' },
              { name: 'Brembo',  light: '#f87171', dark: '#b91c1c' },
              { name: 'MRF',     light: '#a8a29e', dark: '#44403c' },
              { name: 'Valeo',   light: '#8b5cf6', dark: '#4c1d95' },
              { name: 'Monroe',  light: '#fb923c', dark: '#c2410c' },
            ].map((brand, i) => (
              <div key={i} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity cursor-pointer group">
                <div 
                  className="w-5 h-5 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.6)] transition-transform duration-300 group-hover:scale-110" 
                  style={{ background: `radial-gradient(circle at 35% 35%, ${brand.light}, ${brand.dark})` }}
                ></div>
                <span className={`text-lg sm:text-xl font-black ${isDark ? 'text-slate-300' : 'text-slate-800'}`}>{brand.name}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* WHY GARAGES TRUST US */}
      <section className="bg-[#0a0d1a] px-4 py-12 sm:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 text-center sm:mb-12">
            <h2 className="mb-3 text-2xl font-black tracking-tight text-white sm:mb-4 sm:text-3xl md:text-5xl">
              Why Garages & Mechanics Trust SparePe
            </h2>
            <p className="text-sm font-medium text-slate-400 md:text-base">
              Every part on our platform goes through a rigorous verification process
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            
            {/* Card 1 */}
            <div className="rounded-xl border border-slate-800 bg-[#111827] p-4 text-center transition-transform duration-300 sm:rounded-2xl sm:p-8 sm:hover:-translate-y-1">
              <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-400 text-white shadow-lg shadow-emerald-500/20 sm:mb-6 sm:h-12 sm:w-12 sm:rounded-xl">
                <FaCheck size={16} className="sm:text-[22px]" />
              </div>
              <h3 className="mb-1.5 text-xs font-black text-white sm:mb-3 sm:text-lg">100% Genuine Guarantee</h3>
              <p className="hidden text-[13px] font-medium leading-relaxed text-slate-400 sm:block">
                Every product is verified for authenticity. Fake parts get zero tolerance.
              </p>
            </div>

            {/* Card 2 */}
            <div className="rounded-xl border border-slate-800 bg-[#111827] p-4 text-center transition-transform duration-300 sm:rounded-2xl sm:p-8 sm:hover:-translate-y-1">
              <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500 text-white shadow-lg shadow-blue-500/20 sm:mb-6 sm:h-12 sm:w-12 sm:rounded-xl">
                <FaSyncAlt size={14} />
              </div>
              <h3 className="mb-1.5 text-xs font-black text-white sm:mb-3 sm:text-lg">Easy 7-Day Returns</h3>
              <p className="hidden text-[13px] font-medium leading-relaxed text-slate-400 sm:block">
                Wrong part? No problem. Free returns within 7 days, no questions asked.
              </p>
            </div>

            {/* Card 3 */}
            <div className="rounded-xl border border-slate-800 bg-[#111827] p-4 text-center transition-transform duration-300 sm:rounded-2xl sm:p-8 sm:hover:-translate-y-1">
              <div className="mx-auto mb-3 text-2xl leading-none drop-shadow-md sm:mb-6 sm:text-[40px]">🚚</div>
              <h3 className="mb-1.5 text-xs font-black text-white sm:mb-3 sm:text-lg">Same-Day Dispatch</h3>
              <p className="hidden text-[13px] font-medium leading-relaxed text-slate-400 sm:block">
                Order by 2 PM, shipped same day from verified sellers near you.
              </p>
            </div>

            {/* Card 4 */}
            <div className="rounded-xl border border-slate-800 bg-[#111827] p-4 text-center transition-transform duration-300 sm:rounded-2xl sm:p-8 sm:hover:-translate-y-1">
              <div className="mx-auto mb-3 text-2xl leading-none drop-shadow-md sm:mb-6 sm:text-[40px]">♻️</div>
              <h3 className="mb-1.5 text-xs font-black text-white sm:mb-3 sm:text-lg">Used Parts Available</h3>
              <p className="hidden text-[13px] font-medium leading-relaxed text-slate-400 sm:block">
                Quality-checked used parts at 40-60% less. Sustainable and budget-friendly.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="bg-[#0a0d1a] px-3 pb-28 sm:px-4 sm:pb-20">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-800/80 bg-[#0d1425] p-6 text-center sm:rounded-[2rem] sm:p-10 md:p-14">
          {/* Subtle gradient glow */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-full">
            <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-3xl"></div>
          </div>
          
          <div className="relative z-10">
            <h2 className="mb-3 text-xl font-black tracking-tight text-white sm:mb-4 sm:text-2xl md:text-4xl">
              Can't Find Your Part?{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                We'll Source It.
              </span>
            </h2>
            <p className="mx-auto mb-6 max-w-3xl text-sm font-medium text-slate-400 sm:mb-8 md:text-base">
              Tell us what you need and our network of 200+ brands and sellers will find it for you within 24 hours.
            </p>
            <button
              type="button"
              className="mx-auto flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-slate-900 shadow-lg shadow-amber-900/25 transition-colors hover:bg-amber-400 sm:px-8 sm:py-3.5 sm:text-base"
            >
              <span>🔍</span> Request a Part — Free
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};